import type { AdminSelf } from '@saay/api-client';
import { isApiError } from '@saay/api-client';
import {
  formatGroupedAccountNumber,
  formatGroupedCardNumber,
  formatTimestamp,
  type StatusTone,
  toPersianDigits,
} from '@saay/formatting';
import {
  AccordionPanel,
  Button,
  CopyableIdentifier,
  Dialog,
  DialogActions,
  EmptyState,
  Ltr,
  Menu,
  MenuItem,
  SegmentedButtons,
  StatusChip,
  Table,
  useSnackbar,
  useToast,
} from '../kit.js';
import { CardContent, CardHeader, CardTitle, Surface } from '@saay/blocks';
import { useCommand } from '@saay/web-app';
import { type ReactNode, useState } from 'react';
import { Link } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import type {
  AdminBuyer,
  AdminPhysicalCard,
  AdminPhysicalCardIssue,
  AdminPhysicalCardStatusTransition,
  PhysicalCardNumberChange,
  PhysicalCardReissue,
} from '../api.js';
import { AdminCommandFeedback } from '../components/admin-feedback.js';
import { CommandDialog } from '../components/command-dialog.js';
import { useSubmitBlocked } from '../components/form.js';
import { ReasonDialog } from '../components/reason-dialog.js';
import { ReasonField } from '../components/reason-field.js';
import { physicalCardsPath } from '../nav.js';
import { canSeeArea, canUse } from '../permissions.js';
import { useRefreshBuyer } from './buyer-actions.js';
import { displayMaskedCardNumber } from '../display.js';
import { formatAdminPhysicalCardExpiryFa } from '../physical-card-expiry-fa.js';
import { reasonSchema } from '../validation.js';

/**
 * The buyer's SAAY physical card on the buyer sheet (AC §19.6, §31, D-65, D-68, D-117). Since D-117 (9)
 * every reader of the buyer sees the full card and account numbers (current number and each issue);
 * the QR payload and the CVV2 never reach this screen. Block, unblock, reissue, and the number change
 * (K7) wait for the server (`BUYER_PHYSICAL_CARD_MANAGE`).
 */

const ISSUE_REASON: Readonly<Record<AdminPhysicalCardIssue['reasonCode'], string>> = {
  DAMAGED: 'آسیب‌دیده',
  FIRST: 'صدور اول',
  LOST: 'مفقودی',
  NUMBER_CHANGE: 'تغییر شماره',
  RENEWAL: 'تمدید',
};

/** An unknown reason code (a newer contract) reads neutral. */
function issueReasonLabel(code: string): string {
  return ISSUE_REASON[code as AdminPhysicalCardIssue['reasonCode']] ?? 'نامشخص';
}

/** `9900 0000 1100 0017` in ASCII digits (an identifier, LTR), or the raw value if not 16 digits. */
function groupedCard(cardNumber: string): string {
  try {
    return formatGroupedCardNumber(cardNumber);
  } catch {
    return cardNumber;
  }
}

function groupedAccount(accountNumber: string): string {
  try {
    return formatGroupedAccountNumber(accountNumber);
  } catch {
    return accountNumber;
  }
}

const ISSUE_STATUS: Readonly<
  Record<AdminPhysicalCardIssue['status'], { readonly label: string; readonly tone: StatusTone }>
> = {
  ACTIVE: { label: 'فعال', tone: 'success' },
  BLOCKED: { label: 'مسدود', tone: 'danger' },
  REPLACED: { label: 'جایگزین‌شده', tone: 'neutral' },
};

const HOLDER: Readonly<Record<'ADMIN' | 'BUYER', string>> = {
  ADMIN: 'پشتیبانی',
  BUYER: 'خریدار',
};

const REISSUE_REASONS = [
  { label: 'مفقودی', key: 'LOST' },
  { label: 'آسیب‌دیده', key: 'DAMAGED' },
  { label: 'تمدید', key: 'RENEWAL' },
] as const;

const REISSUE_AUDIT_TEXT: Readonly<Record<(typeof REISSUE_REASONS)[number]['key'], string>> = {
  DAMAGED: 'آسیب‌دیده',
  LOST: 'مفقودی',
  RENEWAL: 'تمدید',
};

const DEFAULT_BLOCK_REASON = 'مفقودی';

function cardMaskedTail(masked: string): string {
  const digits = masked.replace(/\D/g, '');
  const tail = digits.slice(-4);
  return tail === '' ? '••••' : toPersianDigits(tail);
}

function cardActionTitle(action: string, buyer: AdminBuyer, card: AdminPhysicalCard): string {
  // «کارت ۹۲۶۴»: masking dots before Persian digits read as zeros («۹۲۶۴۰۰», W4).
  return `${action} کارت ${cardMaskedTail(card.cardNumberMasked)} ${buyer.firstNameFa} ${buyer.lastNameFa}`;
}

function buyerPath(buyerId: string, tail: string): string {
  return `/admin/buyers/${encodeURIComponent(buyerId)}${tail}`;
}

function physicalCardChipTitle(card: AdminPhysicalCard): string {
  const masked = displayMaskedCardNumber(card.cardNumberMasked);
  const tail = masked.replace(/\s/g, '').slice(-4);
  return `کارت ${tail}، ${ISSUE_STATUS[card.status].label}`;
}

function CardBadge({ status }: { readonly status: AdminPhysicalCardIssue['status'] }) {
  const entry = ISSUE_STATUS[status];
  return <StatusChip title={entry.label} tone={entry.tone} />;
}

function cardGuidance(error: unknown): ReactNode {
  if (!isApiError(error)) return undefined;
  switch (error.code) {
    case 'RESOURCE_VERSION_CONFLICT':
      return 'کارت در این فاصله تغییر کرده است؛ اطلاعات به‌روز شد. دوباره ثبت کنید.';
    case 'STATE_CONFLICT':
      return 'وضعیت کارت اجازه این کار را نمی‌دهد. اطلاعات به‌روز شد.';
    default:
      return undefined;
  }
}

/** The K7 refusals that a policy change fixes (AC §31.7); the server text says what to change. */
const NUMBER_CHANGE_POLICY_CODES = new Set([
  'PHYSICAL_CARD_NO_ACTIVE_SERIES',
  'PHYSICAL_CARD_CITY_NOT_CONFIGURED',
  'PHYSICAL_CARD_CITY_CAP_REACHED',
  'PHYSICAL_CARD_SERIAL_EXHAUSTED',
]);

function numberChangeGuidance(error: unknown): ReactNode {
  if (isApiError(error) && NUMBER_CHANGE_POLICY_CODES.has(error.code)) {
    return (
      <>
        {'اول در '}
        <Link className="font-medium underline" to={physicalCardsPath('settings')}>
          تنظیمات شماره‌گذاری کارت‌ها
        </Link>
        {' درستش کنید، بعد دوباره ثبت کنید.'}
      </>
    );
  }
  return cardGuidance(error);
}

function BlockCardDialog({
  buyer,
  buyerId,
  card,
  onOpenChange,
  onSuggestReissue,
}: {
  readonly buyer: AdminBuyer;
  readonly buyerId: string;
  readonly card: AdminPhysicalCard;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSuggestReissue: () => void;
}) {
  const toast = useToast();
  const snackbar = useSnackbar();
  const refresh = useRefreshBuyer(buyerId);
  const offline = useSubmitBlocked();
  const form = useForm<{ reason: string }>({
    defaultValues: { reason: DEFAULT_BLOCK_REASON },
    resolver: zodResolver(z.object({ reason: reasonSchema() })),
  });
  const command = useCommand<string, AdminPhysicalCard>(
    'physical-card.status.BLOCKED',
    (client, reason, intent) =>
      client.post(buyerPath(buyerId, '/physical-card/status-transitions'), {
        body: {
          expectedVersion: card.version,
          reason,
          targetStatus: 'BLOCKED',
        } satisfies AdminPhysicalCardStatusTransition,
        intent,
      }),
    {
      onSuccess: async (result) => {
        await refresh();
        toast.success({ confirmedBy: result, title: 'کارت مسدود شد' });
        onOpenChange(false);
        snackbar.show({
          action: {
            kind: 'open',
            label: 'صدور کارت جدید',
            onAction: onSuggestReissue,
          },
          title: 'کارت مسدود شد',
        });
      },
    },
  );

  async function submit(): Promise<void> {
    if (offline.blocked || command.state.status === 'pending') {
      return;
    }
    await form.handleSubmit(async (values) => {
      const outcome = await command.submit(values.reason.trim());
      if (
        outcome.status === 'error' &&
        isApiError(outcome.error) &&
        (outcome.error.code === 'RESOURCE_VERSION_CONFLICT' ||
          outcome.error.code === 'STATE_CONFLICT')
      ) {
        await refresh();
      }
    })();
  }

  const error = command.state.status === 'error' ? command.state.error : undefined;
  const line = error === undefined ? undefined : cardGuidance(error);

  return (
    <Dialog
      eyebrow="کارت فیزیکی"
      onOpenChange={(open) => {
        if (!open) command.reset();
        onOpenChange(open);
      }}
      open
      title={cardActionTitle('مسدودسازی', buyer, card)}
    >
      <form
        className="admin-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <ReasonField
          error={form.formState.errors.reason?.message}
          intent="physical-card.status.BLOCKED"
          onChange={(value) =>
            form.setValue('reason', value, { shouldDirty: true, shouldValidate: true })
          }
          value={form.watch('reason')}
        />
        <AdminCommandFeedback
          onCheckStatus={() => void command.checkStatus()}
          state={command.state}
        />
        {line === undefined || line === null ? null : (
          <div className="saay-banner" data-tone="warning" role="status">
            <div>{line}</div>
          </div>
        )}
        <DialogActions
          primary={
            <Button
              disabled={offline.blocked}
              loading={command.state.status === 'pending'}
              type="submit"
              variant="danger"
            >
              مسدود کردن
            </Button>
          }
          secondary={
            <Button onClick={() => onOpenChange(false)} type="button" variant="secondary">
              انصراف
            </Button>
          }
        />
      </form>
    </Dialog>
  );
}

function UnblockCardDialog({
  buyerId,
  card,
  onOpenChange,
}: {
  readonly buyerId: string;
  readonly card: AdminPhysicalCard;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const refresh = useRefreshBuyer(buyerId);
  return (
    <ReasonDialog<AdminPhysicalCard>
      eyebrow="کارت فیزیکی"
      guidance={cardGuidance}
      name="physical-card.status.ACTIVE"
      onDone={async (result) => {
        await refresh();
        toast.success({ confirmedBy: result, title: 'مسدودی کارت برداشته شد' });
        onOpenChange(false);
      }}
      onError={async (error) => {
        if (
          isApiError(error) &&
          (error.code === 'RESOURCE_VERSION_CONFLICT' || error.code === 'STATE_CONFLICT')
        ) {
          await refresh();
        }
      }}
      onOpenChange={onOpenChange}
      send={(client, reason, intent) =>
        client.post(buyerPath(buyerId, '/physical-card/status-transitions'), {
          body: {
            expectedVersion: card.version,
            reason,
            targetStatus: 'ACTIVE',
          } satisfies AdminPhysicalCardStatusTransition,
          intent,
        })
      }
      submitLabel="رفع مسدودی"
      title="رفع مسدودی کارت"
      variant="primary"
    />
  );
}

function LostCardDialog({
  buyer,
  buyerId,
  card,
  onBlockOnly,
  onOpenChange,
}: {
  readonly buyer: AdminBuyer;
  readonly buyerId: string;
  readonly card: AdminPhysicalCard;
  readonly onBlockOnly: () => void;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const refresh = useRefreshBuyer(buyerId);
  const offline = useSubmitBlocked();
  const command = useCommand<PhysicalCardReissue, AdminPhysicalCard>(
    'physical-card.lost-reissue',
    (client, body, intent) =>
      client.post(buyerPath(buyerId, '/physical-card/reissues'), { body, intent }),
    {
      onSuccess: async (result) => {
        await refresh();
        toast.success({
          confirmedBy: result,
          description: `انقضا ${formatAdminPhysicalCardExpiryFa(result.data.expiryText)}`,
          title: `نسخهٔ ${toPersianDigits(result.data.cardSequenceNumber)} صادر شد`,
        });
        onOpenChange(false);
      },
    },
  );

  async function reissueLost(): Promise<void> {
    if (offline.blocked || command.state.status === 'pending') {
      return;
    }
    const outcome = await command.submit({
      expectedVersion: card.version,
      reason: REISSUE_AUDIT_TEXT.LOST,
      reasonCode: 'LOST',
    });
    if (
      outcome.status === 'error' &&
      isApiError(outcome.error) &&
      (outcome.error.code === 'RESOURCE_VERSION_CONFLICT' ||
        outcome.error.code === 'STATE_CONFLICT')
    ) {
      await refresh();
    }
  }

  const error = command.state.status === 'error' ? command.state.error : undefined;
  const line = error === undefined ? undefined : cardGuidance(error);

  return (
    <Dialog
      eyebrow="کارت فیزیکی"
      onOpenChange={(open) => {
        if (!open) command.reset();
        onOpenChange(open);
      }}
      open
      title={cardActionTitle('گزارش مفقودی', buyer, card)}
    >
      <AdminCommandFeedback
        onCheckStatus={() => void command.checkStatus()}
        state={command.state}
      />
      {line === undefined || line === null ? null : (
        <div className="saay-banner" data-tone="warning" role="status">
          <div>{line}</div>
        </div>
      )}
      <DialogActions
        primary={
          <Button
            disabled={offline.blocked}
            loading={command.state.status === 'pending'}
            onClick={() => void reissueLost()}
            type="button"
          >
            گزارش مفقودی و صدور کارت جدید
          </Button>
        }
        secondary={
          <Button
            onClick={() => {
              onOpenChange(false);
              onBlockOnly();
            }}
            type="button"
            variant="secondary"
          >
            فقط مسدودسازی
          </Button>
        }
      />
    </Dialog>
  );
}

const reissueSchema = z.object({
  reason: reasonSchema(),
  reasonCode: z.enum(['LOST', 'DAMAGED', 'RENEWAL'], { message: 'دلیل صدور را انتخاب کنید.' }),
});

function ReissueDialog({
  buyerId,
  card,
  onOpenChange,
}: {
  readonly buyerId: string;
  readonly card: AdminPhysicalCard;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const refresh = useRefreshBuyer(buyerId);
  return (
    <CommandDialog<z.infer<typeof reissueSchema>, PhysicalCardReissue, AdminPhysicalCard>
      defaultValues={{ reason: REISSUE_AUDIT_TEXT.LOST, reasonCode: 'LOST' }}
      eyebrow="کارت فیزیکی"
      fields={['reason', 'reasonCode']}
      guidance={cardGuidance}
      name="physical-card.reissue"
      onError={async (error) => {
        if (error.code === 'RESOURCE_VERSION_CONFLICT' || error.code === 'STATE_CONFLICT') {
          await refresh();
        }
      }}
      onOpenChange={onOpenChange}
      onSuccess={async (result) => {
        await refresh();
        toast.success({
          confirmedBy: result,
          description: `انقضا ${formatAdminPhysicalCardExpiryFa(result.data.expiryText)}`,
          title: `نسخهٔ ${toPersianDigits(result.data.cardSequenceNumber)} صادر شد`,
        });
        onOpenChange(false);
      }}
      schema={reissueSchema}
      send={(client, body, intent) =>
        client.post(buyerPath(buyerId, '/physical-card/reissues'), { body, intent })
      }
      submitLabel="صدور مجدد"
      title="صدور مجدد کارت"
      toBody={(values) => ({
        expectedVersion: card.version,
        reason: values.reason.trim(),
        reasonCode: values.reasonCode,
      })}
    >
      {(form) => (
        <>
          <SegmentedButtons
            label="دلیل صدور"
            onChange={(key) => {
              const code = key as (typeof REISSUE_REASONS)[number]['key'];
              form.setValue('reasonCode', code, { shouldDirty: true, shouldValidate: true });
              form.setValue('reason', REISSUE_AUDIT_TEXT[code], {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
            options={REISSUE_REASONS.map((option) => ({
              key: option.key,
              label: option.label,
            }))}
            value={form.watch('reasonCode')}
          />
          <ReasonField
            error={form.formState.errors.reason?.message}
            intent="physical-card.reissue"
            onChange={(value) =>
              form.setValue('reason', value, { shouldDirty: true, shouldValidate: true })
            }
            presets={[]}
            value={form.watch('reason')}
          />
          <p className="admin-muted">
            نسخهٔ فعلی {toPersianDigits(card.cardSequenceNumber)} است. شماره کارت تغییر نمی‌کند.
          </p>
        </>
      )}
    </CommandDialog>
  );
}

const numberChangeSchema = z.object({ reason: reasonSchema() });

/** K7 «تغییر شمارهٔ کارت» (AC §31.8): a new number and a new printed card; no optimistic success. */
export function NumberChangeDialog({
  buyer,
  card,
  onOpenChange,
}: {
  readonly buyer: AdminBuyer;
  readonly card: AdminPhysicalCard;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const refresh = useRefreshBuyer(buyer.buyerId);
  return (
    <CommandDialog<z.infer<typeof numberChangeSchema>, PhysicalCardNumberChange, AdminPhysicalCard>
      defaultValues={{ reason: '' }}
      description="کارت فعلی همین حالا از کار می‌افتد و به خریدار پیامک می‌رود؛ کارت جدید باید چاپ شود."
      eyebrow="کارت فیزیکی"
      fields={['reason']}
      guidance={numberChangeGuidance}
      name="physical-card.number-change"
      onError={async (error) => {
        if (error.code === 'RESOURCE_VERSION_CONFLICT' || error.code === 'STATE_CONFLICT') {
          await refresh();
        }
      }}
      onOpenChange={onOpenChange}
      onSuccess={async (result) => {
        await refresh();
        toast.success({
          confirmedBy: result,
          description: 'کارت جدید را از «کارت‌های فیزیکی › چاپ کارت» بگیرید.',
          title: 'شمارهٔ کارت عوض شد',
        });
        onOpenChange(false);
      }}
      schema={numberChangeSchema}
      send={(client, body, intent) =>
        client.post(buyerPath(buyer.buyerId, '/physical-card/number-changes'), { body, intent })
      }
      submitLabel="تغییر شماره"
      title={cardActionTitle('تغییر شمارهٔ', buyer, card)}
      toBody={(values) => ({
        cardSequenceNumber: card.cardSequenceNumber,
        expectedVersion: card.version,
        reason: values.reason.trim(),
      })}
      variant="danger"
    >
      {(form) => (
        <ReasonField
          error={form.formState.errors.reason?.message}
          intent="physical-card.number-change"
          onChange={(value) =>
            form.setValue('reason', value, { shouldDirty: true, shouldValidate: true })
          }
          value={form.watch('reason')}
        />
      )}
    </CommandDialog>
  );
}

function CardNumberValue({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <CopyableIdentifier
      copyLabel={`کپی ${label}`}
      label={<Ltr>{value}</Ltr>}
      value={value.replace(/\s/g, '')}
    />
  );
}

function CardInfo({ card }: { readonly card: AdminPhysicalCard }) {
  return (
    <dl className="saay-facts">
      <dt>شماره کارت</dt>
      <dd>
        <CardNumberValue label="شماره کارت" value={groupedCard(card.cardNumber)} />
      </dd>
      <dt>شماره حساب</dt>
      <dd>
        <CardNumberValue label="شماره حساب" value={groupedAccount(card.accountNumber)} />
      </dd>
      <dt>سری کارت</dt>
      <dd>{card.seriesPrefix === null ? 'قدیمی (بدون سری)' : <Ltr>{card.seriesPrefix}</Ltr>}</dd>
      <dt>کد شهر</dt>
      <dd>
        {card.cityTag === null ? (
          '—'
        ) : (
          <span className="admin-inline">
            <Ltr>{card.cityTag}</Ltr>
            <span>{`— ${card.cityNameFa ?? ''}`}</span>
          </span>
        )}
      </dd>
    </dl>
  );
}

function CardStatus({ card }: { readonly card: AdminPhysicalCard }) {
  const holder = card.blocked?.byKind;
  return (
    <dl className="saay-facts">
      <dt>وضعیت</dt>
      <dd>
        <CardBadge status={card.status} />
      </dd>
      {card.status === 'BLOCKED' ? (
        <>
          <dt>مسدودکننده</dt>
          <dd>
            {holder === undefined
              ? '—'
              : holder === 'ADMIN' &&
                  card.blocked?.admin !== null &&
                  card.blocked?.admin !== undefined
                ? `${HOLDER.ADMIN} (${card.blocked.admin.displayNameFa})`
                : HOLDER[holder]}
          </dd>
        </>
      ) : null}
      <dt>نسخهٔ جاری</dt>
      <dd>{toPersianDigits(card.cardSequenceNumber)}</dd>
      <dt>انقضا</dt>
      <dd>
        <Ltr>{formatAdminPhysicalCardExpiryFa(card.expiryText)}</Ltr>
      </dd>
      <dt>تاریخ صدور نسخه</dt>
      <dd>{formatTimestamp(card.issuedAt)}</dd>
    </dl>
  );
}

function IssueHistory({ issues }: { readonly issues: readonly AdminPhysicalCardIssue[] }) {
  const table = (
    <Table
      caption="تاریخچه نسخه‌های کارت"
      columns={[
        {
          header: 'نسخه',
          key: 'sequence',
          pin: 'start',
          render: (row) => toPersianDigits(row.cardSequenceNumber),
        },
        {
          header: 'شماره کارت',
          key: 'number',
          render: (row) => <Ltr>{groupedCard(row.cardNumber)}</Ltr>,
        },
        {
          header: 'دلیل',
          key: 'reason',
          render: (row) => issueReasonLabel(row.reasonCode),
        },
        {
          header: 'وضعیت',
          key: 'status',
          render: (row) => <CardBadge status={row.status} />,
        },
        {
          header: 'انقضا',
          key: 'expiry',
          render: (row) => <Ltr>{formatAdminPhysicalCardExpiryFa(row.expiryText)}</Ltr>,
        },
        {
          header: 'صدور',
          key: 'issued',
          render: (row) => formatTimestamp(row.issuedAt),
        },
        {
          header: 'صادرکننده',
          key: 'by',
          render: (row) => row.issuedBy?.displayNameFa ?? 'سیستم',
        },
      ]}
      rowKey={(row) => String(row.cardSequenceNumber)}
      rows={issues}
    />
  );
  if (issues.length <= 1) {
    return table;
  }
  return (
    <AccordionPanel
      items={[{ content: table, key: 'history', title: 'تاریخچه نسخه‌ها' }]}
      label="تاریخچه نسخه‌های کارت"
    />
  );
}

type CardDialog = 'block' | 'lost' | 'number' | 'reissue' | 'unblock';

/** The card actions, once for both menus (the header's and the card tab's); block stays red. */
function cardActionItems(
  card: AdminPhysicalCard,
  renumber: boolean,
  open: (dialog: CardDialog) => void,
): ReactNode {
  return (
    <>
      {card.status === 'BLOCKED' ? (
        <MenuItem onSelect={() => open('unblock')}>رفع مسدودی</MenuItem>
      ) : (
        <>
          <MenuItem destructive onSelect={() => open('block')}>
            مسدودسازی
          </MenuItem>
          <MenuItem onSelect={() => open('lost')}>گزارش مفقودی</MenuItem>
        </>
      )}
      <MenuItem onSelect={() => open('reissue')}>صدور مجدد</MenuItem>
      {renumber ? <MenuItem onSelect={() => open('number')}>تغییر شمارهٔ کارت</MenuItem> : null}
    </>
  );
}

function PhysicalCardDialogs({
  buyer,
  buyerId,
  card,
  dialog,
  onClose,
  onSetDialog,
}: {
  readonly buyer: AdminBuyer;
  readonly buyerId: string;
  readonly card: AdminPhysicalCard;
  readonly dialog: CardDialog | null;
  readonly onClose: () => void;
  readonly onSetDialog: (next: CardDialog) => void;
}) {
  const close = (open: boolean) => {
    if (!open) onClose();
  };
  return (
    <>
      {dialog === 'block' ? (
        <BlockCardDialog
          buyer={buyer}
          buyerId={buyerId}
          card={card}
          onOpenChange={close}
          onSuggestReissue={() => onSetDialog('reissue')}
        />
      ) : null}
      {dialog === 'lost' ? (
        <LostCardDialog
          buyer={buyer}
          buyerId={buyerId}
          card={card}
          onBlockOnly={() => onSetDialog('block')}
          onOpenChange={close}
        />
      ) : null}
      {dialog === 'unblock' ? (
        <UnblockCardDialog buyerId={buyerId} card={card} onOpenChange={close} />
      ) : null}
      {dialog === 'reissue' ? (
        <ReissueDialog buyerId={buyerId} card={card} onOpenChange={close} />
      ) : null}
      {dialog === 'number' ? (
        <NumberChangeDialog buyer={buyer} card={card} onOpenChange={close} />
      ) : null}
    </>
  );
}

/** Chip and overflow menu in the buyer sheet header (ADM-46). */
export function PhysicalCardHeader({
  buyer,
  self,
}: {
  readonly buyer: AdminBuyer;
  readonly self: AdminSelf;
}) {
  const card = buyer.physicalCard;
  const [dialog, setDialog] = useState<CardDialog | null>(null);
  if (card === null) {
    return null;
  }
  const manageable = buyer.status !== 'CLOSED' && canUse(self, 'transitionAdminBuyerPhysicalCard');
  const renumber = buyer.status !== 'CLOSED' && canUse(self, 'changeAdminBuyerPhysicalCardNumber');
  return (
    <>
      <StatusChip title={physicalCardChipTitle(card)} />
      {manageable ? (
        <Menu
          align="end"
          label="کارهای کارت فیزیکی"
          trigger={
            <Button icon="card" size="sm" variant="secondary">
              کارت
            </Button>
          }
        >
          {cardActionItems(card, renumber, setDialog)}
        </Menu>
      ) : null}
      <PhysicalCardDialogs
        buyer={buyer}
        buyerId={buyer.buyerId}
        card={card}
        dialog={dialog}
        onClose={() => setDialog(null)}
        onSetDialog={setDialog}
      />
    </>
  );
}

/** One group of the card tab: a v2 surface with a small heading. */
function CardGroup({
  children,
  id,
  title,
}: {
  readonly children: ReactNode;
  readonly id: string;
  readonly title: string;
}) {
  return (
    <Surface aria-labelledby={id} className="min-w-0" role="region">
      <CardHeader>
        <CardTitle>
          <h4 id={id}>{title}</h4>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex min-w-0 flex-col gap-3">{children}</CardContent>
    </Surface>
  );
}

/**
 * The buyer's «کارت» tab: «اطلاعات کارت» (numbers with copy), «وضعیت», and «سابقهٔ کارت», with the
 * card actions in one menu and a link to the card settings. Shows an empty state until the card is
 * issued.
 */
export function PhysicalCardSection({
  buyer,
  self,
}: {
  readonly buyer: AdminBuyer;
  readonly self?: AdminSelf;
}) {
  const card = buyer.physicalCard;
  const [dialog, setDialog] = useState<CardDialog | null>(null);
  const manageable =
    card !== null &&
    self !== undefined &&
    buyer.status !== 'CLOSED' &&
    canUse(self, 'transitionAdminBuyerPhysicalCard');
  const renumber =
    manageable && self !== undefined && canUse(self, 'changeAdminBuyerPhysicalCardNumber');
  const settings = self !== undefined && canSeeArea(self, 'physicalCards');
  return (
    <section
      aria-labelledby="buyer-physical-card"
      className="flex min-w-0 flex-col gap-4"
      role="region"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-base font-bold" id="buyer-physical-card">
          کارت فیزیکی
        </h3>
        <div className="flex items-center gap-2">
          {settings ? (
            <Link className="text-sm font-medium text-primary" to={physicalCardsPath('summary')}>
              تنظیمات کارت‌ها
            </Link>
          ) : null}
          {manageable ? (
            <Menu
              align="end"
              label="کارهای کارت"
              trigger={
                <Button icon="more" size="sm" variant="secondary">
                  کارها
                </Button>
              }
            >
              {cardActionItems(card, renumber, setDialog)}
            </Menu>
          ) : null}
        </div>
      </div>
      {manageable ? (
        <PhysicalCardDialogs
          buyer={buyer}
          buyerId={buyer.buyerId}
          card={card}
          dialog={dialog}
          onClose={() => setDialog(null)}
          onSetDialog={setDialog}
        />
      ) : null}
      {card === null ? (
        <EmptyState title="کارت فیزیکی هنوز صادر نشده است" />
      ) : (
        <>
          <div className="grid min-w-0 gap-4 lg:grid-cols-2">
            <CardGroup id="buyer-card-info" title="اطلاعات کارت">
              <CardInfo card={card} />
            </CardGroup>
            <CardGroup id="buyer-card-status" title="نسخهٔ فعلی کارت">
              <CardStatus card={card} />
            </CardGroup>
          </div>
          <CardGroup id="buyer-card-history" title="سابقهٔ کارت">
            <IssueHistory issues={card.issues} />
          </CardGroup>
        </>
      )}
    </section>
  );
}
